import React, { useState, useEffect } from "react"
import { computeFunnelData } from "../../../utils/funnelHelpers"
import FunnelChart from "../../../components/ui/funnel-chart"

const CHART_COLOR_VARS = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5"]

// Cycles through the 5 chart color tokens, wrapping back to --chart-1 if there
// are more stages than colors (6 stages when Product Views is tracked).
const colorFor = (i) => `var(${CHART_COLOR_VARS[i % CHART_COLOR_VARS.length]})`

const LearningFunnelSection = ({ orgId }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    computeFunnelData(orgId).then((result) => {
      setData(result)
      setLoading(false)
    })
  }, [orgId])

  if (loading || !data) {
    return <div className="h-72 bg-muted rounded-lg animate-pulse mb-6" />
  }

    const chartData = data?.stages?.map((stage, i) => ({
    label: stage?.label,
    value: stage?.value,
    gradient: [
      { offset: "0%", color: colorFor(i) },
      { offset: "100%", color: colorFor(i + 1) },
    ],
  }))

      // Mobile-only: one card per funnel stage (not paired transitions) — same
  // FunnelChart, just fed a single-stage array per slide instead of two.
  const funnelSlides = data?.stages?.map((stage, i) => ({
    key: stage?.key,
    title: stage?.label,
    conversion: i === 0 ? null : stage?.stepConversion,
    segments: [chartData?.[i]],
  }))

  return (
    <div className="bg-card border border-border rounded-lg p-5 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold text-foreground">Learning Funnel</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        The full learner journey from first view to credential, not just completion rate.
      </p>

      {!data?.viewsTracked && (
        <div className="text-xs text-muted-foreground bg-muted rounded-md px-3 py-2 mb-5">
          Product view tracking isn't wired in yet, so the funnel starts at Enrollments.
        </div>
      )}

            <div className="hidden md:block">
        <FunnelChart data={chartData} orientation="horizontal" layers={3} gap={6} grid={{ bands: true, lines: true }} />
      </div>

                  <div className="md:hidden -mx-5 px-5 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {funnelSlides?.map((slide) => (
          <div key={slide.key} className="shrink-0 w-[70%] snap-center bg-muted/40 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-foreground truncate">{slide.title}</h4>
              {slide.conversion !== null && (
                <span
                  className={
                    slide.conversion >= 50
                      ? "text-success text-xs font-semibold"
                      : "text-xs font-semibold text-muted-foreground"
                  }
                >
                  {slide.conversion}%
                </span>
              )}
            </div>
            <FunnelChart data={slide.segments} orientation="horizontal" layers={3} gap={6} grid={{ bands: true, lines: true }} />
          </div>
        ))}
      </div>

      {/* Step-by-step conversion — the chart's own % pill is relative to the top
          of the funnel; this list is the sequential stage-to-stage conversion
          the original spec asked for. */}
                  <div className="mt-6 pt-4 border-t border-border overflow-hidden">
        <div className="flex w-max animate-funnel-marquee">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex gap-8 pr-8 shrink-0">
              {data?.stages?.map((stage, i) =>
                i === 0 ? null : (
                  <div key={stage?.key} className="text-xs text-muted-foreground whitespace-nowrap">
                    <span className="text-foreground font-medium">{data?.stages?.[i - 1]?.label}</span>
                    {" → "}
                    <span className="text-foreground font-medium">{stage?.label}</span>:{" "}
                    <span className={stage?.stepConversion >= 50 ? "text-success font-semibold" : "font-semibold"}>
                      {stage?.stepConversion}%
                    </span>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
        <style>{`
          @keyframes funnel-marquee {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }
          .animate-funnel-marquee {
            animation: funnel-marquee 18s linear infinite;
          }
        `}</style>
      </div>
    </div>
  )
}

export default LearningFunnelSection